import React, { useState, useEffect } from 'react';
import { Box, Typography, CircularProgress, Alert, Card, CardContent, Chip, Divider, Grid } from '@mui/material';
import { resumeService, Resume, ResumeListApiResponse } from '../../services/resumeService';
import { COLORS } from '../../config/colors';

interface ResumeScreeningContentProps {
  candidate?: any;
  candidateId?: string;
  onContentChange?: (contentId: string) => void;
  onActionClick?: (action: string, data?: any) => void;
}

const ResumeScreeningContent: React.FC<ResumeScreeningContentProps> = ({ 
  candidate, 
  candidateId, 
  onContentChange, 
  onActionClick 
}) => {
  const [resumeData, setResumeData] = useState<Resume | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchResumeData = async () => {
      // Get resume_id from candidate object or use candidateId
      const resumeId = candidate?.resume_id || candidate?.id || candidateId;
      
      if (!resumeId) {
        console.log('[ResumeScreeningContent] No resume ID available');
        return;
      }

      setLoading(true);
      setError(null);

      try {
        console.log('[ResumeScreeningContent] Fetching resume data for ID:', resumeId);
        const response: ResumeListApiResponse = await resumeService.getResumeByResumeId(resumeId);
        
        if (response.items && response.items.length > 0) {
          const resume = response.items[0];
          setResumeData(resume);
          console.log('[ResumeScreeningContent] Resume data fetched successfully:', resume);
        } else {
          setError('No resume data found');
          console.warn('[ResumeScreeningContent] No resume data found for ID:', resumeId);
        }
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to fetch resume data';
        setError(errorMessage);
        console.error('[ResumeScreeningContent] Error fetching resume data:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchResumeData();
  }, [candidate, candidateId]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h4" sx={{ fontFamily: 'var(--font-heading)', color: 'var(--primary-blue)', mb: 2 }}>
        🔍 Advanced Resume Screening
      </Typography>
      <Typography sx={{ fontFamily: 'var(--font-body)', color: 'var(--dark-gray)', mb: 3 }}>
        Intelligent resume analysis and candidate matching against job requirements.
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {resumeData && (
        <Card sx={{ mb: 3, border: '1px solid #e0e7ff', boxShadow: '0 2px 4px rgba(0,0,0,0.05)' }}>
          <CardContent>
            <Typography variant="h6" sx={{ fontFamily: 'var(--font-heading)', color: 'var(--primary-blue)', mb: 3 }}>
              📄 Resume Details
            </Typography>
            
            {/* Basic Information */}
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle2" sx={{ color: 'var(--primary-blue)', fontWeight: 700, mb: 2, textTransform: 'uppercase', fontSize: '0.85rem' }}>
                Basic Information
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <Typography variant="body2" sx={{ color: 'var(--dark-gray)', fontWeight: 600, mb: 0.5 }}>
                    Candidate Name
                  </Typography>
                  <Typography variant="body1" sx={{ color: 'var(--primary-blue)', fontWeight: 500 }}>
                    {resumeData.candidateName}
                  </Typography>
                </Grid>
                
                <Grid item xs={12} md={6}>
                  <Typography variant="body2" sx={{ color: 'var(--dark-gray)', fontWeight: 600, mb: 0.5 }}>
                    Email
                  </Typography>
                  <Typography variant="body1">
                    {resumeData.candidateEmail || resumeData.email || 'Not provided'}
                  </Typography>
                </Grid>
                
                <Grid item xs={12} md={6}>
                  <Typography variant="body2" sx={{ color: 'var(--dark-gray)', fontWeight: 600, mb: 0.5 }}>
                    Phone
                  </Typography>
                  <Typography variant="body1">
                    {resumeData.phone || 'Not provided'}
                  </Typography>
                </Grid>
                
                <Grid item xs={12} md={6}>
                  <Typography variant="body2" sx={{ color: 'var(--dark-gray)', fontWeight: 600, mb: 0.5 }}>
                    Location
                  </Typography>
                  <Typography variant="body1">
                    {(resumeData as any).location || 'Not specified'}
                  </Typography>
                </Grid>
                
                <Grid item xs={12} md={6}>
                  <Typography variant="body2" sx={{ color: 'var(--dark-gray)', fontWeight: 600, mb: 0.5 }}>
                    Experience
                  </Typography>
                  <Typography variant="body1" sx={{ color: 'var(--primary-blue)', fontWeight: 500 }}>
                    {(resumeData as any).experience_years ? `${(resumeData as any).experience_years} years` : 'Not specified'}
                  </Typography>
                </Grid>
                
                <Grid item xs={12} md={6}>
                  <Typography variant="body2" sx={{ color: 'var(--dark-gray)', fontWeight: 600, mb: 0.5 }}>
                    Status
                  </Typography>
                  <Chip 
                    label={resumeData.status} 
                    size="small" 
                    sx={{ 
                      mt: 0.5,
                      bgcolor: resumeData.status === 'approved' ? '#4caf50' : 
                               resumeData.status === 'rejected' ? '#f44336' : 
                               resumeData.status === 'submitted' ? '#ff9800' : '#2196f3',
                      color: 'white',
                      fontWeight: 600,
                      textTransform: 'capitalize'
                    }} 
                  />
                </Grid>
              </Grid>
            </Box>

            <Divider sx={{ my: 3 }} />

            {/* Skills */}
            {(resumeData as any).skills && (resumeData as any).skills.length > 0 && (
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle2" sx={{ color: 'var(--primary-blue)', fontWeight: 700, mb: 2, textTransform: 'uppercase', fontSize: '0.85rem' }}>
                  Skills
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {(resumeData as any).skills.map((skill: string, index: number) => (
                    <Chip
                      key={index}
                      label={skill}
                      size="small"
                      sx={{
                        bgcolor: COLORS.INFO_LIGHT,
                        color: 'var(--primary-blue)',
                        fontWeight: 500,
                        '&:hover': {
                          bgcolor: '#bbdefb',
                        }
                      }}
                    />
                  ))}
                </Box>
              </Box>
            )}

            {/* Education */}
            {(resumeData as any).education && (resumeData as any).education.length > 0 && (
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle2" sx={{ color: 'var(--primary-blue)', fontWeight: 700, mb: 2, textTransform: 'uppercase', fontSize: '0.85rem' }}>
                  Education
                </Typography>
                {(resumeData as any).education.map((edu: any, index: number) => (
                  <Card key={index} sx={{ mb: 1.5, bgcolor: '#f8f9fa', boxShadow: 'none', border: '1px solid #e0e7ff' }}>
                    <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                      <Typography variant="body1" sx={{ fontWeight: 600, color: 'var(--primary-blue)', mb: 0.5 }}>
                        {edu.degree}
                      </Typography>
                      <Typography variant="body2" sx={{ color: 'var(--dark-gray)' }}>
                        Graduated: {edu.graduation_year}
                      </Typography>
                    </CardContent>
                  </Card>
                ))}
              </Box>
            )}

            <Divider sx={{ my: 3 }} />

            {/* IDs and Metadata */}
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" sx={{ color: 'var(--primary-blue)', fontWeight: 700, mb: 2, textTransform: 'uppercase', fontSize: '0.85rem' }}>
                Metadata
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <Typography variant="body2" sx={{ color: 'var(--dark-gray)', fontWeight: 600, mb: 0.5 }}>
                    Resume ID
                  </Typography>
                  <Typography variant="body1" sx={{ fontFamily: 'monospace', fontSize: '0.9rem', color: 'var(--primary-blue)' }}>
                    {resumeData.id}
                  </Typography>
                </Grid>
                
                <Grid item xs={12} md={6}>
                  <Typography variant="body2" sx={{ color: 'var(--dark-gray)', fontWeight: 600, mb: 0.5 }}>
                    Job ID
                  </Typography>
                  <Typography variant="body1" sx={{ fontFamily: 'monospace', fontSize: '0.9rem', color: 'var(--primary-blue)' }}>
                    {resumeData.jobId}
                  </Typography>
                </Grid>
                
                {resumeData.jobTitle && (
                  <Grid item xs={12} md={6}>
                    <Typography variant="body2" sx={{ color: 'var(--dark-gray)', fontWeight: 600, mb: 0.5 }}>
                      Job Title
                    </Typography>
                    <Typography variant="body1">
                      {resumeData.jobTitle}
                    </Typography>
                  </Grid>
                )}
                
                {resumeData.score !== undefined && resumeData.score !== null && (
                  <Grid item xs={12} md={6}>
                    <Typography variant="body2" sx={{ color: 'var(--dark-gray)', fontWeight: 600, mb: 0.5 }}>
                      Score
                    </Typography>
                    <Typography variant="h6" sx={{ color: 'var(--primary-blue)', fontWeight: 700 }}>
                      {resumeData.score}%
                    </Typography>
                  </Grid>
                )}
                
                {resumeData.fileName && (
                  <Grid item xs={12} md={6}>
                    <Typography variant="body2" sx={{ color: 'var(--dark-gray)', fontWeight: 600, mb: 0.5 }}>
                      File Name
                    </Typography>
                    <Typography variant="body1" sx={{ fontSize: '0.9rem' }}>
                      {resumeData.fileName}
                    </Typography>
                  </Grid>
                )}
                
                {resumeData.uploadedDate && (
                  <Grid item xs={12} md={6}>
                    <Typography variant="body2" sx={{ color: 'var(--dark-gray)', fontWeight: 600, mb: 0.5 }}>
                      Uploaded Date
                    </Typography>
                    <Typography variant="body1">
                      {new Date(resumeData.uploadedDate).toLocaleDateString('en-US', { 
                        year: 'numeric', 
                        month: 'long', 
                        day: 'numeric' 
                      })}
                    </Typography>
                  </Grid>
                )}
              </Grid>
            </Box>

            {/* Panel List */}
            {(resumeData as any).panel_list && (resumeData as any).panel_list.length > 0 && (
              <>
                <Divider sx={{ my: 3 }} />
                <Box>
                  <Typography variant="subtitle2" sx={{ color: 'var(--primary-blue)', fontWeight: 700, mb: 2, textTransform: 'uppercase', fontSize: '0.85rem' }}>
                    Interview Panel
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {(resumeData as any).panel_list.map((panelist: string, index: number) => (
                      <Chip
                        key={index}
                        label={panelist}
                        size="medium"
                        sx={{
                          bgcolor: COLORS.SUCCESS_LIGHT,
                          color: COLORS.SUCCESS_DARK,
                          fontWeight: 500
                        }}
                      />
                    ))}
                  </Box>
                </Box>
              </>
            )}

            {/* Notes */}
            {(resumeData as any).notes && (resumeData as any).notes.trim() !== '' && (
              <>
                <Divider sx={{ my: 3 }} />
                <Box>
                  <Typography variant="subtitle2" sx={{ color: 'var(--primary-blue)', fontWeight: 700, mb: 2, textTransform: 'uppercase', fontSize: '0.85rem' }}>
                    Notes
                  </Typography>
                  <Typography variant="body1" sx={{ color: 'var(--dark-gray)', whiteSpace: 'pre-wrap' }}>
                    {(resumeData as any).notes}
                  </Typography>
                </Box>
              </>
            )}
          </CardContent>
        </Card>
      )}

      
    </Box>
  );
};

export default ResumeScreeningContent;

